"use client"

import { YOUTUBE_VIDEO } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import youtubeVideoService from "@/services/youtube-video"
import { VideoData } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { App } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useState } from "react"

interface UseYouTubeVideoReturn {
  loading: boolean
  fetchVideoById: (id: number) => Promise<VideoData | null>
  createVideo: (payload: any) => Promise<boolean>
  updateVideo: (id: string, payload: any) => Promise<boolean>
  deleteVideo: (id: number) => Promise<boolean>
  hasPermission: (action: string) => boolean
  handleBack: () => void
}

export const useYouTubeVideo = (): UseYouTubeVideoReturn => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [loading, setLoading] = useState(false)
  const videoServices = youtubeVideoService()
  const { message } = App.useApp()

  const fetchVideoById = useCallback(
    async (id: number): Promise<VideoData | null> => {
      setLoading(true)
      try {
        const video = await videoServices.fetchVideoById(id)
        return video
      } catch (error) {
        message.error(t("Lỗi khi tải thông tin video. Vui lòng thử lại."))
        return null
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const createVideo = useCallback(
    async (payload: any): Promise<boolean> => {
      setLoading(true)
      try {
        const response = await videoServices.addVideo({
          data: payload,
        })
        if (response) {
          message.success(t("Tạo video thành công!"))
          const targetPath = createPath(YOUTUBE_VIDEO.LIST.PATH, searchParams)
          router.push(targetPath)
          return true
        }
        return false
      } catch (error) {
        message.error(t("Lỗi khi tạo video. Vui lòng thử lại."))
        return false
      } finally {
        setLoading(false)
      }
    },
    [videoServices, router, searchParams],
  )

  const updateVideo = useCallback(
    async (id: string, payload: any): Promise<boolean> => {
      setLoading(true)
      try {
        const response = await videoServices.updateVideo(Number(id), {
          data: payload,
        })
        if (response) {
          message.success(t("Cập nhật video thành công!"))
          const targetPath = createPath(YOUTUBE_VIDEO.LIST.PATH, searchParams)
          router.push(targetPath)
          return true
        }
        return false
      } catch (error) {
        message.error(t("Lỗi khi cập nhật video. Vui lòng thử lại."))
        return false
      } finally {
        setLoading(false)
      }
    },
    [videoServices, router, searchParams],
  )

  const deleteVideo = useCallback(
    async (id: number): Promise<boolean> => {
      setLoading(true)
      try {
        const response = await videoServices.deleteVideo(id)
        if (response) {
          message.success(t("Xóa video thành công!"))
          return true
        }
        return false
      } catch (error) {
        message.error(t("Lỗi khi xóa video. Vui lòng thử lại."))
        return false
      } finally {
        setLoading(false)
      }
    },
    [videoServices],
  )

  const handleBack = useCallback(() => {
    const targetPath = createPath(YOUTUBE_VIDEO.LIST.PATH, searchParams)
    router.push(targetPath)
  }, [router, searchParams])

  const { hasPermission: checkPermission } = useCheckPermission()
  const hasPermission = useCallback((action: string): boolean => {
    return checkPermission("video", action)
  }, [checkPermission])

  return {
    loading,
    fetchVideoById,
    createVideo,
    updateVideo,
    deleteVideo,
    hasPermission,
    handleBack,
  }
}